import { getMetaData } from "../../utils/Constant";
import { useState, useEffect } from "react";

const JSONFormatGuide = () => {
  const [copiedCode, setCopiedCode] = useState("");

  const copyToClipboard = (code, label) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(label);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  useEffect(() => {
    const metaData = getMetaData("json-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-4 mb-4">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        <div className="row justify-content-center">
          {/* Header */}
          <header
            className="text-center py-5 px-3 position-relative ps-5 pe-5"
            style={{
              color: "#fff",
              background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            }}
          >
            <h1 className="display-5 fw-bold text-white mb-3 lh-sm">
              JSON Explained: The Developer's Favorite Data Format
            </h1>
            <div className="text-white mb-4">
              <span className="me-3">• Productivity Tips</span>
              <span>• Best Practices</span>
            </div>

            <div className="mt-4 position-relative">
              <span className="badge bg-light text-dark fs-6 me-2 p-2">
                Web Development
              </span>
              <span className="badge bg-light text-dark fs-6 p-2">
                Data Formats
              </span>
            </div>
          </header>

          <section className="p-4 p-md-5">
            {/* Summary */}
            <section className="mb-5">
              <div className="card border-start-4 border-start-primary shadow-sm">
                <div className="card-body">
                  <h6 className="card-title text-uppercase fw-bold text-black mb-2">
                    Quick Summary
                  </h6>
                  <p className="card-text mb-0">
                    If you’ve ever worked with web APIs, configuration files, or
                    modern apps, chances are you’ve come across JSON. It’s the
                    unofficial standard for exchanging data between systems —
                    simple, flexible, and universally understood.
                  </p>
                </div>
              </div>
            </section>

            {/* What is JSON */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-primary me-3">1</span>
                    What Exactly Is JSON?
                  </h2>
                </div>
                <div className="card-body">
                  <p className="lead mb-4">
                    JSON stands for <strong>JavaScript Object Notation</strong>,
                    but don’t get misled — it’s not just for JavaScript. JSON is
                    a simple text-based format that makes it easy for both
                    humans and machines to understand data.
                  </p>

                  <div className="card bg-light border-0 mb-4">
                    <div className="card-body p-3">
                      <h5 className="card-title mb-2">💡 Quick Example</h5>
                      <p className="card-text mb-0">
                        Here’s how a basic user profile might look in JSON:
                      </p>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-2">
                      <small>user-profile.json</small>
                      <button
                        className="btn btn-sm btn-outline-light"
                        onClick={() =>
                          copyToClipboard(
                            `{
  "user": {
    "id": 12345,
    "name": "Alice Smith",
    "email": "alice.smith@example.com",
    "isActive": true,
    "hobbies": ["reading", "cycling", "hiking"],
    "address": {
      "street": "123 Main St",
      "city": "Boston",
      "country": "USA"
    }
  }
}`,
                            "user profile"
                          )
                        }
                      >
                        {copiedCode === "user profile" ? "✓ Copied!" : "Copy"}
                      </button>
                    </div>
                    <div className="card-body p-3">
                      <pre
                        className="mb-0"
                        style={{ fontSize: "0.85rem", lineHeight: "1.3" }}
                      >
                        {`{
  "user": {
    "id": 12345,
    "name": "Alice Smith",
    "email": "alice.smith@example.com",
    "isActive": true,
    "hobbies": ["reading", "cycling", "hiking"],
    "address": {
      "street": "123 Main St",
      "city": "Boston",
      "country": "USA"
    }
  }
}`}
                      </pre>
                    </div>
                  </div>

                  <p className="mb-0">
                    It’s clean, easy to scan, and instantly understandable — that’s 
                    why developers often call JSON “the language of the web.”
                  </p>
                </div>
              </div>
            </section>

            {/* JSON in Action */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-success me-3">2</span>
                    Where You’ll See JSON in Action
                  </h2>
                </div>
                <div className="card-body">
                  <p className="lead mb-4">
                    JSON is everywhere in web development. You’ll see it in APIs,
                    apps, and configuration files. Here are some common examples:
                  </p>

                  <div className="row g-4 mb-4">
                    <div className="col-md-6">
                      <div className="card h-100 border-primary border-2">
                        <div className="card-body">
                          <h5 className="card-title fw-bold text-primary mb-3">
                            API Communication
                          </h5>
                          <p>
                            Whenever your app talks to a backend or an API,
                            JSON usually carries the data — it’s the standard for
                            exchanging information between servers and browsers.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100 border-success border-2">
                        <div className="card-body">
                          <h5 className="card-title fw-bold text-success mb-3">
                            Configuration Files
                          </h5>
                          <p>
                            Popular files like <code>package.json</code> in Node.js
                            use JSON to store data about your project —
                            everything from dependencies to script commands.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h5 className="fw-bold mt-4 mb-3">
                    How Developers Use JSON in Code
                  </h5>
                  <p className="mb-4">
                    JSON is supported natively by almost every programming language.
                    Let’s look at how it works in JavaScript and Python:
                  </p>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-dark text-white py-2">
                          <small>JavaScript Example</small>
                        </div>
                        <div className="card-body p-3">
                          <pre
                            style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
                            className="mb-3"
                          >
                            {`// Convert JSON string to object
const jsonString = '{"name": "Alice", "age": 30}';
const user = JSON.parse(jsonString);
console.log(user.name); // "Alice"

// Convert object to JSON string
const newUser = { name: "Bob", age: 25 };
const jsonOutput = JSON.stringify(newUser);`}
                          </pre>
                          <button
                            className="btn btn-outline-primary btn-sm w-100"
                            onClick={() =>
                              copyToClipboard(
                                `const jsonString = '{"name": "Alice", "age": 30}';
const user = JSON.parse(jsonString);
console.log(user.name);
const newUser = { name: "Bob", age: 25 };
const jsonOutput = JSON.stringify(newUser);`,
                                "javascript example"
                              )
                            }
                          >
                            {copiedCode === "javascript example"
                              ? "✓ Copied!"
                              : "Copy Code"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-dark text-white py-2">
                          <small>Python Example</small>
                        </div>
                        <div className="card-body p-3">
                          <pre
                            style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
                            className="mb-3"
                          >
                            {`import json
user_data = json.loads('{"name": "Alice", "age": 30}')
print(user_data["name"]) # Alice
new_user = {"name": "Bob", "age": 25}
json_output = json.dumps(new_user)`}
                          </pre>
                          <button
                            className="btn btn-outline-primary btn-sm w-100"
                            onClick={() =>
                              copyToClipboard(
                                `import json
user_data = json.loads('{"name": "Alice", "age": 30}')
print(user_data["name"])
new_user = {"name": "Bob", "age": 25}
json_output = json.dumps(new_user)`,
                                "python example"
                              )
                            }
                          >
                            {copiedCode === "python example"
                              ? "✓ Copied!"
                              : "Copy Code"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Pros and Cons */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-warning me-3">3</span>
                    The Good and The Not-So-Good
                  </h2>
                </div>
                <div className="card-body">
                  <p className="lead mb-4">
                    Like any technology, JSON has its strengths and weaknesses.
                    Knowing these will help you decide when to use it.
                  </p>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="card h-100 border-success border-2">
                        <div className="card-header bg-success text-white py-3">
                          <h5 className="mb-0">👍 Why Developers Love JSON</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <strong className="d-block">Easy to Read</strong>
                            <small className="text-muted">
                              Unlike older formats like XML, JSON is clean and
                              human-friendly.
                            </small>
                          </div>
                          <div className="mb-3">
                            <strong className="d-block">Lightweight</strong>
                            <small className="text-muted">
                              Small file sizes mean faster data transfer.
                            </small>
                          </div>
                          <div className="mb-3">
                            <strong className="d-block">
                              Universal Support
                            </strong>
                            <small className="text-muted">
                              Works with almost every programming language.
                            </small>
                          </div>
                          <div>
                            <strong className="d-block">Structured Data</strong>
                            <small className="text-muted">
                              Perfect for representing complex, nested
                              information.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card h-100 border-warning border-2">
                        <div className="card-header bg-warning text-dark py-3">
                          <h5 className="mb-0">👎 Where JSON Falls Short</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <strong className="d-block">No Comments</strong>
                            <small className="text-muted">
                              You can’t add notes to explain your data.
                            </small>
                          </div>
                          <div className="mb-3">
                            <strong className="d-block">
                              Limited Data Types
                            </strong>
                            <small className="text-muted">
                              No built-in support for dates, functions, or
                              custom types.
                            </small>
                          </div>
                          <div className="mb-3">
                            <strong className="d-block">
                              Strict Formatting
                            </strong>
                            <small className="text-muted">
                              One missing comma or bracket can break everything.
                            </small>
                          </div>
                          <div>
                            <strong className="d-block">
                              Manual Creation is Tedious
                            </strong>
                            <small className="text-muted">
                              Writing complex JSON by hand can be time-consuming.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* The Problem */}
            <section className="mb-5">
              <div className="card bg-primary text-white border-0">
                <div className="card-body p-4 text-center">
                  <h3 className="h5 fw-bold mb-3">
                    The Real Problem: Creating JSON Data
                  </h3>
                  <p className="mb-3">
                    While JSON itself is easy to understand, creating
                    realistic, complex datasets for testing and development can
                    be a real headache.
                  </p>
                  <p className="mb-4">
                    Manually typing user profiles, product catalogs, or API
                    responses is slow and prone to errors — leaving less time
                    for actual coding.
                  </p>

                  <div className="bg-white text-dark rounded p-3 mb-3">
                    <h6 className="fw-bold mb-2">
                      Imagine needing data like this:
                    </h6>
                    <div className="bg-dark text-light rounded p-2">
                      <pre
                        style={{ fontSize: "0.7rem", lineHeight: "1.2" }}
                        className="mb-0"
                      >
                        {`{
  "users": [
    {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "profile": {
        "firstName": "Maria",
        "lastName": "Garcia",
        "email": "maria.garcia@company.com"
      }
    }
  ]
}`}
                      </pre>
                    </div>
                    <small className="text-muted mt-2 d-block">
                      Writing this by hand means worrying about valid UUIDs,
                      realistic names, and proper emails — it quickly adds up.
                    </small>
                  </div>
                </div>
              </div>
            </section>

            {/* Our Solution */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <h3 className="h5 fw-bold text-center mb-3">
                    Meet Your New Favorite Tool
                  </h3>

                  <p className="text-center mb-4">
                    We built the <strong>Advanced Fake JSON Generator</strong> to
                    make creating data easy and fast. Think of it as your
                    personal assistant that handles the boring stuff so you can
                    focus on building great things.
                  </p>

                  <div className="row g-4 mb-4">
                    <div className="col-md-4 text-center">
                      <div
                        className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <span>⚡</span>
                      </div>
                      <h6>Instant Data</h6>
                      <small className="text-muted">
                        Generate realistic JSON in seconds, not hours.
                      </small>
                    </div>
                    <div className="col-md-4 text-center">
                      <div
                        className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <span>🎯</span>
                      </div>
                      <h6>Realistic Content</h6>
                      <small className="text-muted">
                        Get valid emails, proper names, and real-looking addresses.
                      </small>
                    </div>
                    <div className="col-md-4 text-center">
                      <div
                        className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <span>🔧</span>
                      </div>
                      <h6>Flexible Output</h6>
                      <small className="text-muted">Single objects or arrays of data.</small>
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="mb-3">
                      Whether you’re testing APIs, building UI prototypes, or
                      filling dev databases, our generator helps you work smarter,
                      not harder.
                    </p>
                    <a
                      href="https://samplefiles.dev/advanced-fake-json-generator/"
                      className="btn btn-primary px-4"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Try the JSON Generator Now
                    </a>
                    <small className="text-muted mt-2 d-block">
                      It’s free and could save you hours every week.
                    </small>
                  </div>
                </div>
              </div>
            </section>

            {/* Final CTA */}
            <section className="text-center">
              <small className="text-muted">
                Ready to stop manually creating JSON data?
                <a
                  href="https://samplefiles.dev/advanced-fake-json-generator/"
                  className="text-decoration-none ms-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Give our generator a try →
                </a>
              </small>
            </section>

            {/* Resources */}
            <footer className="border-top pt-4 mt-5">
              <h6 className="fw-bold text-dark mb-3">Related Topics & Tags</h6>
              <div className="d-flex flex-wrap gap-2">
                <span className="badge bg-secondary">#JSONGuide</span>
                <span className="badge bg-secondary">#WebDevelopment</span>
                <span className="badge bg-secondary">#APIs</span>
                <span className="badge bg-secondary">#DataFormats</span>
                <span className="badge bg-secondary">#Programming</span>
                <span className="badge bg-secondary">#DevelopmentTools</span>
                <span className="badge bg-secondary">#JSONGenerator</span>
                <span className="badge bg-secondary">#Productivity</span>
              </div>
            </footer>
          </section>
        </div>
      </article>
    </div>
  );
};

export default JSONFormatGuide;
